import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import client, { unwrap } from "@/api/client";
import { FolderKanban, FileCheck, Sparkles, PlusCircle } from "lucide-react";

const ACTIVITY_LABEL = {
  PROJECT_CREATED: { label: "Project created", icon: PlusCircle },
  QUOTATION_SENT: { label: "Quotation sent", icon: FileCheck },
  QUOTATION_APPROVED: { label: "Quotation approved", icon: Sparkles },
};

function formatINR(n) {
  if (!n) return "₹0";
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

export default function CustomerDetailPage() {
  const { customerId } = useParams();

  const { data: customer } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => client.get(`/customers/${customerId}`).then(unwrap),
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["customer-projects", customerId],
    queryFn: async () => {
      const list = await client.get(`/projects?customerId=${customerId}`).then(unwrap);
      const withValue = await Promise.all(
        list.map(async (p) => {
          const quotations = await client.get(`/quotations?projectId=${p._id}`).then(unwrap).catch(() => []);
          const value = quotations.reduce((sum, q) => sum + (q.grandTotal || 0), 0);
          return { ...p, value };
        }),
      );
      return withValue;
    },
    enabled: !!customerId,
  });

  const { data: activity = [] } = useQuery({
    queryKey: ["customer-activity", customerId],
    queryFn: () => client.get(`/customers/${customerId}/activity`).then(unwrap),
  });

  const customerSince = customer?.createdAt ? new Date(customer.createdAt).getFullYear() : null;

  return (
    <div data-testid="customer-detail-page">
      <div className="mb-8">
        <h1 className="font-serif-display text-3xl text-[#2A211D]" data-testid="customer-detail-name">{customer?.name}</h1>
        {customerSince && <p className="text-sm text-[#6E6259] mt-1">Customer since {customerSince}</p>}
        <div className="flex gap-4 mt-2 text-sm text-[#8C8379]">
          {customer?.phone && <span>{customer.phone}</span>}
          {customer?.email && <span>{customer.email}</span>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <p className="text-[10px] font-semibold tracking-widest text-[#8C8379] uppercase mb-3">Projects</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="customer-projects-grid">
            {projects.map((p) => (
              <Link
                key={p._id}
                to={`/projects/${p._id}`}
                data-testid={`customer-project-card-${p._id}`}
                className="bg-white border border-[#E6DDD3] hover:border-[#C85A32] rounded-xl p-5 transition-colors"
              >
                <FolderKanban className="h-4 w-4 text-[#8C8379] mb-2" strokeWidth={1.75} />
                <p className="font-heading font-semibold text-[#2A211D]">{p.name}</p>
                <p className="text-xs text-[#8C8379] mt-1">
                  {[p.city, p.bhk].filter(Boolean).join(" · ") || p.address}
                </p>
                <p className="text-sm font-medium text-[#C85A32] mt-3">{formatINR(p.value)}</p>
              </Link>
            ))}
            {projects.length === 0 && <p className="text-sm text-[#8C8379]">No projects yet for this customer.</p>}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-semibold tracking-widest text-[#8C8379] uppercase mb-3">Recent Activity</p>
          <div className="bg-white border border-[#E6DDD3] rounded-xl divide-y divide-[#F0EAE0]" data-testid="customer-activity-timeline">
            {activity.length === 0 && <p className="px-4 py-6 text-sm text-[#8C8379] text-center">No activity yet.</p>}
            {activity.map((a) => {
              const meta = ACTIVITY_LABEL[a.action] || { label: a.action, icon: Sparkles };
              const Icon = meta.icon;
              return (
                <div key={a._id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-item-${a._id}`}>
                  <Icon className="h-4 w-4 text-[#C85A32] mt-0.5 shrink-0" strokeWidth={1.75} />
                  <div>
                    <p className="text-sm text-[#2A211D]">{meta.label}</p>
                    <p className="text-xs text-[#8C8379] mt-0.5">
                      {new Date(a.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
